import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { safeStorage } from '@/lib/storage'

export type AppLanguage = 'en' | 'hi'
export const LOCALE_STORAGE_KEY = 'leazepro.locale'
export const LANGUAGES: { value: AppLanguage; label: string; nativeLabel: string }[] = [
  { value: 'en', label: 'English', nativeLabel: 'English' },
  { value: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी' },
]

/** The browser's preference decides the first visit; the switch in Settings persists after that. */
export function detectLanguage(navigatorLanguage: string | undefined = typeof navigator === 'undefined' ? undefined : navigator.language): AppLanguage {
  return navigatorLanguage?.toLowerCase().startsWith('hi') ? 'hi' : 'en'
}

interface LocaleState {
  language: AppLanguage
  setLanguage: (l: AppLanguage) => void
}

export const useLocaleStore = create<LocaleState>()(
  persist((set) => ({ language: detectLanguage(), setLanguage: (language) => set({ language }) }), { name: LOCALE_STORAGE_KEY, storage: createJSONStorage(() => safeStorage) }),
)

// Tells the server too (a push is composed with no request in flight, so Accept-Language cannot reach it), then reloads.
// The reload cancels in-flight requests, so the call is awaited first but capped: a toggle must not hang on a bad connection.
// `syncToServer` is injected so this stays testable without a network.
export async function changeLanguage(
  next: AppLanguage,
  syncToServer: (l: AppLanguage) => Promise<unknown>,
  reload: () => void = () => window.location.reload(),
  timeoutMs = 1500,
): Promise<void> {
  if (next === currentLanguage()) return
  useLocaleStore.getState().setLanguage(next)

  await Promise.race([
    syncToServer(next).catch(() => undefined),
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ])

  // Copy, Intl formatters and Accept-Language are read at module load; a reload is the honest switch.
  reload()
}

export const currentLanguage = (): AppLanguage => useLocaleStore.getState().language

/** BCP 47 tag for Intl and Accept-Language; India is the market on both languages. */
export const appLocale = (): string => (currentLanguage() === 'hi' ? 'hi-IN' : 'en-IN')
